import React, { useState, useEffect, useMemo } from "react";
import supabase from "../../supabaseClient";
import { useSelector } from "react-redux";
import axios from "axios";
import SelectedCard from '../../components/SelectedCard';
import BillingNotificationsBell from "../../components/dashboard/BillingNotificationsBell";
import InactiveShipmentButton from "../../components/dashboard/InactiveShipmentButton";

const Dashboard = () => {
  const user = useSelector((state) => state.auth.user);
  const userId = user.id;
  const [shipmentCount, setShipmentCount] = useState(0);
  const [upcomingShipmentsCount, setUpcomingShipmentsCount] = useState(0);
  const [activeShipments, setActiveShipmentsCount] = useState(0);
  const [completedShipments, setCompletedShipmentsCount] = useState(0);
  const [billedShipments, setBilledShipmentsCount] = useState(0);
  const [selectedCard, setSelectedCard] = useState("active");
  // eslint-disable-next-line no-unused-vars
  const [totalShipment, setTotalShipment] = useState("");
  // eslint-disable-next-line no-unused-vars
  const [upcomingShipments, setUpcomingShipments] = useState(null);

  // Fetch shipment count and upcoming shipments count
  useEffect(() => {
    fetchOverallTotalShipment();
    fetchActiveShipments();
    fetchUpcomingShipments();
    fetchCompletedShipments();
  }, []);

  useEffect(() => {
    if (userId && user?.role && user?.department) {
      const fetchData = async () => {
        try {
          let url = "";

          if (user.role === "Corporate") {
            url = `https://track-trace-server-production.up.railway.app/api/dashboard/corporate-dashboard?department=${user.department}`;
          } else {
            url = `https://track-trace-server-production.up.railway.app/api/dashboard?userId=${userId}&role=${user.role}&department=${user.department}`;
          }

          const response = await fetch(url);
          const data = await response.json();

          if (!data.error) {
            console.log("📊 Dashboard API response:", data);
            setShipmentCount(data.shipmentCount);
            setUpcomingShipmentsCount(data.upcomingShipmentsCount);
            setActiveShipmentsCount(data.activeShipmentsCount);
            setCompletedShipmentsCount(data.completedShipmentsCount);
            setBilledShipmentsCount(data.billedShipmentsCount || 0);
          } else {
            console.warn("❌ Dashboard API returned error:", data.error);
          }
        } catch (error) {
          console.error("Error fetching dashboard data:", error);
        }
      };

      // Initial fetch
      fetchData();

      // Refresh every 30 seconds (instead of every 1 second)
      const interval = setInterval(fetchData, 30000);

      return () => clearInterval(interval);
    }
  }, [userId, user?.role, user?.department]);

  /** ================================
   * 🔹 Setup Supabase Real-time Listener
   * ================================= */
  useEffect(() => {
    if (!userId) return;

    const subscription = supabase
      .channel("shipments-realtime")
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "shipments" },
        async (payload) => {
          console.log("Real-time update received:", payload);

          // Refetch updated data from API after any change
          try {
            const response = await fetch(
              `https://track-trace-server-production.up.railway.app/api/dashboard/corporate-dashboard?department=${user.department}`
            );
            const data = await response.json();

            if (!data.error) {
              setShipmentCount(data.shipmentCount);
              setUpcomingShipmentsCount(data.upcomingShipmentsCount);
              setActiveShipmentsCount(data.activeShipmentsCount);
              setCompletedShipmentsCount(data.completedShipmentsCount);
              setBilledShipmentsCount(data.billedShipmentsCount);
            }
          } catch (error) {
            console.error("Error updating dashboard data:", error);
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(subscription);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  const fetchOverallTotalShipment = async () => {
    try {
      const { data } = await axios.get(
        "https://track-trace-server-production.up.railway.app/api/dashboard/corporate-dashboard/overall-shipments"
      );
      setTotalShipment(data.overallTotalShipments);
    } catch (error) {
      console.error("Error fetching total shipments:", error);
    }
  };
  const fetchActiveShipments = async () => {
    try {
      const { data } = await axios.get(
        "https://track-trace-server-production.up.railway.app/api/dashboard/corporate-dashboard/active-shipments"
      );
      setActiveShipmentsCount(data.activeShipments);
      // console.log("Fetch active shipments: ", data);
    } catch (error) {
      console.log("Error fetching active shipments:", error);
    }
  };

  const fetchUpcomingShipments = async () => {
    try {
      const { data } = await axios.get(
        "https://track-trace-server-production.up.railway.app/api/dashboard/corporate-dashboard/upcoming-shipments"
      );
      setUpcomingShipments(data.upcomingShipments);
    } catch (error) {
      console.log("Error fetching upcoming shipments:", error);
    }
  };

  const fetchCompletedShipments = async () => {
    try {
      const { data } = await axios(
        "https://track-trace-server-production.up.railway.app/api/dashboard/corporate-dashboard/completed-shipments"
      );
      setCompletedShipmentsCount(data.completedShipments);
    } catch (error) {
      console.log("Error fetching completed shipments:", error);
    }
  };

  return (
    <section id="dashboard" className="flex flex-col justify-center font-poppins min-h-screen">
      <div className="flex flex-col bg-gray-100 justify-center p-[5px] gap-[30px]">
        <div className="flex flex-col gap-4 rounded-2xl border border-gray-100 bg-white px-5 py-5 shadow-lg sm:flex-row sm:items-center sm:justify-between">
          <div>
            <p className="text-xs font-semibold uppercase tracking-widest text-gray-500">
              Dashboard Overview
            </p>
            <h1 className="text-2xl font-bold text-blue sm:text-3xl">
              Corporate Dashboard
            </h1>
          </div>
          <div className="flex items-center gap-3">
            <InactiveShipmentButton
              user={user}
              shipmentsPath="/corporate/corporate-shipment-management"
            />
            <BillingNotificationsBell user={user} />
          </div>
        </div>

        {useMemo(() => (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-[40px] md:gap-[20px] w-full">
          {/* Active Shipments */}
          <div 
            className={`group relative overflow-hidden flex flex-col w-full px-[18px] py-[20px] rounded-2xl cursor-pointer transition-all duration-300 transform hover:scale-[1.02] hover:shadow-xl ${
              selectedCard === "active" 
                ? 'bg-blue text-white shadow-blue/25' 
                : 'bg-white/90 backdrop-blur-sm border border-gray-100 hover:border-blue/30 shadow-lg'
            }`}
            onClick={() => setSelectedCard("active")}
          >
            {/* Background decoration */}
            <div className="absolute top-0 right-0 w-24 h-24 bg-yellow/10 rounded-full blur-2xl opacity-50"></div>
            
            <div className="relative z-10">
              <div className="flex flex-col items-start mb-3">
                <div className={`p-3 rounded-xl mb-2 ${
                  selectedCard === "active" ? 'bg-white/20' : 'bg-blue/10'
                }`}>
                  <i className={`fas fa-shipping-fast text-[28px] ${
                    selectedCard === "active" ? 'text-white' : 'text-blue'
                  }`}></i>
                </div>
              </div>
              <div className="flex flex-row justify-between w-full mb-3">
                <h1 className={`font-bold text-[28px] ${
                  selectedCard === "active" ? 'text-white' : 'text-blue'
                }`}>
                  {activeShipments || '0'}
                </h1>
                {selectedCard === "active" && (
                  <div className="w-2 h-2 bg-yellow rounded-full animate-pulse mt-2"></div>
                )}
              </div>
              <h1 className={`font-medium text-[16px] ${
                selectedCard === "active" ? 'text-blue-100' : 'text-grey'
              }`}>
                Active Shipments
              </h1>
            </div>
          </div>

          {/* Upcoming Shipments */}
          <div 
            className={`group relative overflow-hidden flex flex-col w-full px-[18px] py-[20px] rounded-2xl cursor-pointer transition-all duration-300 transform hover:scale-[1.02] hover:shadow-xl ${
              selectedCard === "upcoming" 
                ? 'bg-blue text-white shadow-blue/25' 
                : 'bg-white/90 backdrop-blur-sm border border-gray-100 hover:border-blue/30 shadow-lg'
            }`}
            onClick={() => setSelectedCard("upcoming")} 
          >
            {/* Background decoration */}
            <div className="absolute top-0 right-0 w-24 h-24 bg-yellow/10 rounded-full blur-2xl opacity-50"></div>
            
            <div className="relative z-10">
              <div className="flex flex-col items-start mb-3">
                <div className={`p-3 rounded-xl mb-2 ${
                  selectedCard === "upcoming" ? 'bg-white/20' : 'bg-blue/10'
                }`}>
                  <i className={`fa-solid fa-hourglass-half text-[28px] ${
                    selectedCard === "upcoming" ? 'text-white' : 'text-blue'
                  }`}></i>
                </div>
              </div>
              <div className="flex flex-row justify-between w-full mb-3">
                <h1 className={`font-bold text-[28px] ${
                  selectedCard === "upcoming" ? 'text-white' : 'text-blue'
                }`}>
                  {upcomingShipmentsCount || '0'}
                </h1>
                {selectedCard === "upcoming" && (
                  <div className="w-2 h-2 bg-yellow rounded-full animate-pulse mt-2"></div>
                )}
              </div>
              <h1 className={`font-medium text-[16px] ${
                selectedCard === "upcoming" ? 'text-blue-100' : 'text-grey'
              }`}>
                Shipment Nomination
              </h1>
            </div>
          </div>

          {/* Total Shipments */}
          <div 
            className={`group relative overflow-hidden flex flex-col w-full px-[18px] py-[20px] rounded-2xl cursor-pointer transition-all duration-300 transform hover:scale-[1.02] hover:shadow-xl ${
              selectedCard === "total" 
                ? 'bg-blue text-white shadow-blue/25' 
                : 'bg-white/90 backdrop-blur-sm border border-gray-100 hover:border-blue/30 shadow-lg'
            }`}
            onClick={() => setSelectedCard("total")}
          >
            {/* Background decoration */}
            <div className="absolute top-0 right-0 w-24 h-24 bg-yellow/10 rounded-full blur-2xl opacity-50"></div>
            
            <div className="relative z-10">
              <div className="flex flex-col items-start mb-3">
                <div className={`p-3 rounded-xl mb-2 ${
                  selectedCard === "total" ? 'bg-white/20' : 'bg-blue/10'
                }`}>
                  <i className={`fa-solid fa-chart-simple text-[28px] ${
                    selectedCard === "total" ? 'text-white' : 'text-blue'
                  }`}></i>
                </div>
              </div>
              <div className="flex flex-row justify-between w-full mb-3">
                <h1 className={`font-bold text-[28px] ${
                  selectedCard === "total" ? 'text-white' : 'text-blue'
                }`}>
                  {shipmentCount || '0'}
                </h1>
                {selectedCard === "total" && (
                  <div className="w-2 h-2 bg-yellow rounded-full animate-pulse mt-2"></div>
                )}
              </div>
              <h1 className={`font-medium text-[16px] ${
                selectedCard === "total" ? 'text-blue-100' : 'text-grey'
              }`}>
                Total Shipments
              </h1>
            </div>
          </div>

          {/* Completed Shipments */}
          <div 
            className={`group relative overflow-hidden flex flex-col w-full px-[18px] py-[20px] rounded-2xl cursor-pointer transition-all duration-300 transform hover:scale-[1.02] hover:shadow-xl ${
              selectedCard === "completed" 
                ? 'bg-blue text-white shadow-blue/25' 
                : 'bg-white/90 backdrop-blur-sm border border-gray-100 hover:border-blue/30 shadow-lg'
            }`}
            onClick={() => setSelectedCard("completed")}
          >
            {/* Background decoration */}
            <div className="absolute top-0 right-0 w-24 h-24 bg-yellow/10 rounded-full blur-2xl opacity-50"></div>
            
            <div className="relative z-10">
              <div className="flex flex-col items-start mb-3">
                <div className={`p-3 rounded-xl mb-2 ${
                  selectedCard === "completed" ? 'bg-white/20' : 'bg-blue/10'
                }`}>
                  <i className={`fas fa-circle-check text-[28px] ${
                    selectedCard === "completed" ? 'text-white' : 'text-blue'
                  }`}></i>
                </div>
              </div>
              <div className="flex flex-row justify-between w-full mb-3">
                <h1 className={`font-bold text-[28px] ${
                  selectedCard === "completed" ? 'text-white' : 'text-blue'
                }`}>
                  {completedShipments || '0'}
                </h1>
                {selectedCard === "completed" && (
                  <div className="w-2 h-2 bg-yellow rounded-full animate-pulse mt-2"></div>
                )}
              </div>
              <h1 className={`font-medium text-[16px] ${
                selectedCard === "completed" ? 'text-blue-100' : 'text-grey'
              }`}>
                Delivered Shipments
              </h1>
            </div>
          </div>

          {/* Billed Shipments */}
          <div
            className={`group relative overflow-hidden flex flex-col w-full px-[18px] py-[20px] rounded-2xl cursor-pointer transition-all duration-300 transform hover:scale-[1.02] hover:shadow-xl ${
              selectedCard === "billed"
                ? 'bg-blue text-white shadow-blue/25'
                : 'bg-white/90 backdrop-blur-sm border border-gray-100 hover:border-blue/30 shadow-lg'
            }`}
            onClick={() => setSelectedCard("billed")}
          >
            {/* Background decoration */}
            <div className="absolute top-0 right-0 w-24 h-24 bg-yellow/10 rounded-full blur-2xl opacity-50"></div>

            <div className="relative z-10">
              <div className="flex flex-col items-start mb-3">
                <div className={`p-3 rounded-xl mb-2 ${
                  selectedCard === "billed" ? 'bg-white/20' : 'bg-blue/10'
                }`}>
                  <i className={`fas fa-file-invoice-dollar text-[28px] ${
                    selectedCard === "billed" ? 'text-white' : 'text-blue'
                  }`}></i>
                </div>
              </div>
              <div className="flex flex-row justify-between w-full mb-3">
                <h1 className={`font-bold text-[28px] ${
                  selectedCard === "billed" ? 'text-white' : 'text-blue'
                }`}>
                  {billedShipments || '0'}
                </h1>
                {selectedCard === "billed" && (
                  <div className="w-2 h-2 bg-yellow rounded-full animate-pulse mt-2"></div>
                )}
              </div>
              <h1 className={`font-medium text-[16px] ${
                selectedCard === "billed" ? 'text-blue-100' : 'text-grey'
              }`}>
                Billed Shipments
              </h1>
            </div>
          </div>
          </div>
        ), [selectedCard, activeShipments, upcomingShipmentsCount, shipmentCount, completedShipments, billedShipments])}

        {/* Selected Card Component */}
        <div className="bg-white/90 backdrop-blur-sm rounded-2xl p-6 shadow-lg border border-gray-100">
          {useMemo(() => <SelectedCard selectedCard={selectedCard}/>, [selectedCard])}
        </div>

        

       
      </div>
    </section>
  );
};

export default Dashboard;
